import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '@core/environments/environments';
import { StorageMap } from '@ngx-pwa/local-storage';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private isBrowser: boolean;

  /**
   * Constructor for the StorageService.
   * @param _storage The storage map.
   * @param platformId The platform id.
   * Initializes the isBrowser flag based on the platform id.
   */
  constructor(
    public _storage: StorageMap,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Gets the value of a key from local storage.
   * If the function is called on the server, it will return null.
   * @param key The key to retrieve from local storage.
   * @returns The value of the key from local storage.
   */
  async getStorage(key: string) {
    if (!this.isBrowser) return null; // Skip on server
    return await lastValueFrom(this._storage.get(`${environment.appName}-${key}`));
  }

  /**
   * Sets the value of a key in local storage.
   * If the function is called on the server, it will return without doing anything.
   * @param key The key to set in local storage.
   * @param value The value to set in local storage. It can be an object, string, number, or boolean.
   */
  async setStorage(key: string, value: object | string | number | boolean) {
    if (!this.isBrowser) return; // Skip on server
    await lastValueFrom(this._storage.set(`${environment.appName}-${key}`, value));
  }

  /**
   * Deletes a key from local storage.
   * If the function is called on the server, it will return without doing anything.
   * @param key The key to delete from local storage.
   */
  async deleteStorage(key: string) {
    if (!this.isBrowser) return; // Skip on server
    await lastValueFrom(this._storage.delete(`${environment.appName}-${key}`));
  }

  /**
   * Checks if a key exists in local storage.
   * If the function is called on the server, it will return false.
   * @param key The key to check in local storage.
   * @returns A promise that resolves to true if the key exists in local storage, otherwise false.
   */
  async hasStorage(key: string) {
    if (!this.isBrowser) return false; // Skip on server
    return await lastValueFrom(this._storage.has(`${environment.appName}-${key}`));
  }

  /**
   * Watches for changes to a key in local storage.
   * If the function is called on the server, it will return an empty observable.
   * @param key The key to watch in local storage.
   * @returns An observable that emits the value of the key from local storage whenever it changes.
   */
  watchStorage(key: string) {
    return this._storage.watch(`${environment.appName}-${key}`);
  }

  /**
   * Update an item in the storage array by its id.
   * If the item doesn't exist, it will be added.
   * @param key The key of the storage array.
   * @param updatedItem The item to update with.
   * @param updateById A function that takes an item from the storage array and the updatedItem, and returns true if the item should be updated, false otherwise.
   */
  async updateStorage<T>(
    key: string,
    updatedItem: T,
    updateById: (item: T, updatedItem: T) => boolean,
  ) {
    const currentData = ((await this.getStorage(key)) as T[]) || [];

    if (Array.isArray(currentData)) {
      const updatedArray = currentData.map((item) => {
        if (updateById(item, updatedItem)) {
          return { ...item, ...updatedItem };
        }
        return item;
      });

      const exists = currentData.some((item) => updateById(item, updatedItem));
      if (!exists) {
        updatedArray.push(updatedItem);
      }

      await this.setStorage(key, updatedArray);
    } else {
      await this.setStorage(key, [updatedItem]);
    }
  }
}
